/**
 * @fileoverview Protege um formulário com alterações não salvas: enquanto
 * `dirty`, sair da página pede confirmação.
 *
 * - Links e history.push/replace (menu lateral, menu principal, redirecionamentos):
 *   a navegação fica parada e `confirmOpen` abre o modal da tela; `confirm()`
 *   segue para onde ia, `cancel()` fica.
 * - Voltar/avançar do navegador: o history v4 só consegue perguntar com o
 *   confirm nativo (`message`).
 * - Recarregar ou fechar a aba: aviso do próprio navegador (beforeunload).
 * - Botão da própria tela que sai dela (ex.: voltar): `leave(navegar)`.
 *
 * Trocar só o #trecho da URL (mesma página) não pergunta; trocar a busca (?…),
 * como sair do painel de uma pessoa para a lista, pergunta. O history v4 aceita
 * um bloqueio por vez: use um guard por tela, não um por formulário.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";

export default function useLeaveGuard(dirty, message) {
  const history = useHistory();
  const [pending, setPending] = useState(null);
  const unblockRef = useRef(null);

  useEffect(() => {
    if (!dirty) return undefined;

    const onBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);

    const unblock = history.block((location, action) => {
      if (location.pathname === history.location.pathname && location.search === history.location.search) return undefined;
      if (action === "POP") return message;
      // O objeto location inteiro preserva o #trecho e o state do destino.
      setPending({ run: () => (action === "REPLACE" ? history.replace(location) : history.push(location)) });
      return false;
    });
    unblockRef.current = unblock;

    return () => {
      window.removeEventListener("beforeunload", onBeforeUnload);
      unblock();
      unblockRef.current = null;
    };
  }, [dirty, history, message]);

  const leave = useCallback(
    (run) => {
      if (!dirty) {
        run();
        return;
      }
      setPending({ run });
    },
    [dirty]
  );

  const confirm = useCallback(() => {
    if (!pending) return;
    setPending(null);
    // Libera o bloqueio antes de navegar; senão a própria navegação seria barrada.
    if (unblockRef.current) {
      unblockRef.current();
      unblockRef.current = null;
    }
    pending.run();
  }, [pending]);

  const cancel = useCallback(() => setPending(null), []);

  return { leave, confirmOpen: !!pending, confirm, cancel };
}

/**
 * Lado do formulário: avisa o pai (onDirtyChange, opcional) quando há
 * alterações não salvas e manda false ao sair da tela.
 */
export function useReportDirty(dirty, onDirtyChange) {
  const callback = useRef(onDirtyChange);
  callback.current = onDirtyChange;

  useEffect(() => {
    if (callback.current) callback.current(dirty);
  }, [dirty]);

  useEffect(
    () => () => {
      if (callback.current) callback.current(false);
    },
    []
  );
}
